import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import { setErrorTranslator } from '@noor/api';
import uzRbac from './locales/uz/rbac.json';
import ruRbac from './locales/ru/rbac.json';
import enRbac from './locales/en/rbac.json';
import ruErrors from './locales/ru/errors.json';
import enErrors from './locales/en/errors.json';
import uzErrors from './locales/uz/errors.json';
import ruCommon from './locales/ru/common.json';
import ruOrders from './locales/ru/orders.json';
import ruInbox from './locales/ru/inbox.json';
import ruCourier from './locales/ru/courier.json';
import ruMerchants from './locales/ru/merchants.json';
import ruModeration from './locales/ru/moderation.json';
import ruBusiness from './locales/ru/business.json';
import ruUsers from './locales/ru/users.json';
import ruSettings from './locales/ru/settings.json';
import ruReports from './locales/ru/reports.json';
import ruSlot from './locales/ru/slot.json';
import enCommon from './locales/en/common.json';
import enOrders from './locales/en/orders.json';
import enInbox from './locales/en/inbox.json';
import enCourier from './locales/en/courier.json';
import enMerchants from './locales/en/merchants.json';
import enModeration from './locales/en/moderation.json';
import enBusiness from './locales/en/business.json';
import enUsers from './locales/en/users.json';
import enSettings from './locales/en/settings.json';
import enReports from './locales/en/reports.json';
import enSlot from './locales/en/slot.json';
import uzCommon from './locales/uz/common.json';
import uzOrders from './locales/uz/orders.json';
import uzInbox from './locales/uz/inbox.json';
import uzCourier from './locales/uz/courier.json';
import uzMerchants from './locales/uz/merchants.json';
import uzModeration from './locales/uz/moderation.json';
import uzBusiness from './locales/uz/business.json';
import uzUsers from './locales/uz/users.json';
import uzSettings from './locales/uz/settings.json';
import uzReports from './locales/uz/reports.json';
import uzSlot from './locales/uz/slot.json';

/**
 * i18n setup. The platform targets a RU/UZ/EN market; Russian is the source of
 * truth (cpanel was hardcoded Russian) and the default/fallback language. UI
 * strings live under namespaced resources (`common`, `orders`) and are read via
 * `t('orders:…')`. The active language is a durable pref in the `ui-store`; the
 * provider reads it on boot and a `<LanguageSwitcher>` calls
 * `i18n.changeLanguage`.
 */
export const I18N_NAMESPACES = [
  'common',
  'orders',
  'inbox',
  'courier',
  'merchants',
  'moderation',
  'business',
  'users',
  'rbac',
  'settings',
  'reports',
  'slot',
  // Backend error catalog: one key per numeric code the API can return. Owned
  // by `@noor/api`'s `error-catalog.ts` — see `setErrorTranslator` below.
  'errors',
] as const;

const resources = {
  ru: {
    common: ruCommon,
    orders: ruOrders,
    inbox: ruInbox,
    courier: ruCourier,
    merchants: ruMerchants,
    moderation: ruModeration,
    business: ruBusiness,
    users: ruUsers,
    settings: ruSettings,
    reports: ruReports,
    slot: ruSlot,
    rbac: ruRbac,
    errors: ruErrors,
  },
  en: {
    common: enCommon,
    orders: enOrders,
    inbox: enInbox,
    courier: enCourier,
    merchants: enMerchants,
    moderation: enModeration,
    business: enBusiness,
    users: enUsers,
    settings: enSettings,
    reports: enReports,
    slot: enSlot,
    rbac: enRbac,
    errors: enErrors,
  },
  uz: {
    common: uzCommon,
    orders: uzOrders,
    inbox: uzInbox,
    courier: uzCourier,
    merchants: uzMerchants,
    moderation: uzModeration,
    business: uzBusiness,
    users: uzUsers,
    settings: uzSettings,
    reports: uzReports,
    slot: uzSlot,
    rbac: uzRbac,
    errors: uzErrors,
  },
} as const;

/** Read the persisted language pref synchronously so boot uses the right lang. */
function initialLanguage(): string {
  if (typeof localStorage === 'undefined') return 'ru';
  try {
    const raw = localStorage.getItem('ui-prefs');
    if (!raw) return 'ru';
    const parsed = JSON.parse(raw) as { state?: { language?: string } };
    return parsed.state?.language ?? 'ru';
  } catch {
    return 'ru';
  }
}

void i18n.use(initReactI18next).init({
  resources,
  lng: initialLanguage(),
  fallbackLng: 'ru',
  defaultNS: 'common',
  ns: I18N_NAMESPACES,
  interpolation: { escapeValue: false },
});

// Teach the API layer to translate backend error CODES. `getApiErrorMessage`
// consults this before the call site's own fallback, so every existing error
// toast became localized without being touched.
//
// Returns `undefined` for a key this app has no string for (a code newer than
// the bundle) rather than i18next's default of echoing the key back — that is
// what lets resolution fall through to the caller's fallback instead of
// rendering `errors:order.notFound` at an operator.
setErrorTranslator((key) => {
  const translated = i18n.t(key, { ns: 'errors', defaultValue: '' });
  return translated === '' ? undefined : translated;
});

export { i18n };
